import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import type { AcademyTrainingsListingResponse } from '~/types/academy/academy';
import type { RelatedTrainingsMode } from '~/types/academy/courseRelations';
import { withResolvedCtaUrl, withTeaserColor } from '~/utils/academy/trainings/enrichTrainingsListingItem';

export function useAcademyRelatedTrainings(
    courseId: MaybeRefOrGetter<string>,
    mode: MaybeRefOrGetter<RelatedTrainingsMode> = 'same_catalog',
    relatedCourseIds: MaybeRefOrGetter<number[]> = []
) {
    const localizedPath = useLocalizedPath();
    const academyPath = useRequiredSectionPathById('academy');
    const { urlSegment } = useLanguage();

    const query = computed(() => {
        const ids = toValue(relatedCourseIds);

        return {
            mode: toValue(mode),
            ...(ids.length > 0 ? { course_ids: ids.join(',') } : {})
        };
    });

    const { data, error, status, pending } = useFetch<AcademyTrainingsListingResponse>(
        () => `/api/academy/trainings/${toValue(courseId)}/related`,
        {
            credentials: 'include',
            server: false,
            query
        }
    );

    const items = computed(() =>
        (data.value?.items ?? [])
            .map(withTeaserColor)
            .map((item) => withResolvedCtaUrl(item, localizedPath, urlSegment.value, academyPath.value))
    );
    const hasItems = computed(() => items.value.length > 0);
    const prettyError = computed(() => (error.value ? JSON.stringify(error.value, null, 2) : null));
    const isInitialLoading = computed(() => pending.value && items.value.length === 0);

    return {
        data,
        error,
        prettyError,
        isInitialLoading,
        status,
        items,
        hasItems
    };
}
